import React, { ReactChild } from "react";
import { addToDate } from "../../helpers/date-helper";
import { Asset, HighlightHour } from "../../types/public-types";
import styles from "./grid.module.css";

const DEFAULT_HIGHLIGHT_COLOR = "#1976d2";
const DEFAULT_HIGHLIGHT_STROKE_WIDTH = 2;

export type GridBodyProps = {
	assets: Asset[];
	dates: Date[];
	svgWidth: number;
	rowHeight: number;
	columnWidth: number;
	todayColor: string;
	highlightHours?: HighlightHour[];
};
export const GridBody: React.FC<GridBodyProps> = ({
	assets,
	dates,
	rowHeight,
	svgWidth,
	columnWidth,
	todayColor,
	highlightHours,
}) => {
	let y = 0;
	const gridRows: ReactChild[] = [];
	const rowLines: ReactChild[] = [
		<line
			key="RowLineFirst"
			x="0"
			y1={0}
			x2={svgWidth}
			y2={0}
			className={styles.gridRowLine}
		/>,
	];
	for (const asset of assets) {
		gridRows.push(
			<rect
				key={"Row" + asset.id}
				data-asset-id={asset.id}
				x="0"
				y={y}
				width={svgWidth}
				height={rowHeight}
				className={styles.gridRow}
			/>,
		);
		rowLines.push(
			<line
				key={"RowLine" + asset.id}
				x="0"
				y1={y + rowHeight}
				x2={svgWidth}
				y2={y + rowHeight}
				className={styles.gridRowLine}
			/>,
		);
		y += rowHeight;
	}

	const now = new Date();
	let tickX = 0;
	const ticks: ReactChild[] = [];
	const highlightLines: ReactChild[] = [];
	let today: ReactChild = <rect />;

	// Calculate highlight lines for specified hours
	if (dates.length >= 2 && highlightHours && highlightHours.length > 0) {
		const startTime = dates[0].getTime();
		const endTime = dates[dates.length - 1].getTime();
		const msPerColumn = dates[1].getTime() - dates[0].getTime();

		for (const highlight of highlightHours) {
			const {
				hour,
				color = DEFAULT_HIGHLIGHT_COLOR,
				strokeWidth = DEFAULT_HIGHLIGHT_STROKE_WIDTH,
			} = highlight;

			// Find first occurrence of this hour after start
			const firstOccurrence = new Date(dates[0]);
			if (firstOccurrence.getHours() >= hour) {
				// Move to next day if we've passed this hour today
				firstOccurrence.setDate(firstOccurrence.getDate() + 1);
			}
			firstOccurrence.setHours(hour, 0, 0, 0);

			// Add all occurrences within range
			let occurrence = firstOccurrence;
			while (occurrence.getTime() < endTime) {
				const occurrenceX =
					((occurrence.getTime() - startTime) / msPerColumn) * columnWidth;
				if (occurrenceX > 0) {
					highlightLines.push(
						<line
							key={`highlight-${hour}-${occurrence.getTime()}`}
							x1={occurrenceX}
							y1={0}
							x2={occurrenceX}
							y2={y}
							stroke={color}
							strokeWidth={strokeWidth}
						/>,
					);
				}
				// Move to next day
				occurrence = new Date(occurrence.getTime() + 24 * 60 * 60 * 1000);
			}
		}
	}

	for (let i = 0; i < dates.length; i++) {
		const date = dates[i];
		ticks.push(
			<line
				key={date.getTime()}
				x1={tickX}
				y1={0}
				x2={tickX}
				y2={y}
				className={styles.gridTick}
			/>,
		);
		if (
			(i + 1 !== dates.length &&
				date.getTime() < now.getTime() &&
				dates[i + 1].getTime() >= now.getTime()) ||
			// if current date is last
			(i !== 0 &&
				i + 1 === dates.length &&
				date.getTime() < now.getTime() &&
				addToDate(
					date,
					date.getTime() - dates[i - 1].getTime(),
					"millisecond",
				).getTime() >= now.getTime())
		) {
			today = (
				<rect
					x={tickX}
					y={0}
					width={columnWidth}
					height={y}
					fill={todayColor}
				/>
			);
		}
		tickX += columnWidth;
	}
	return (
		<g className="gridBody">
			<g className="rows">{gridRows}</g>
			<g className="rowLines">{rowLines}</g>
			<g className="ticks">{ticks}</g>
			<g className="highlightLines">{highlightLines}</g>
			<g className="today">{today}</g>
		</g>
	);
};
